import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { dateToTodayElapsedSeconds, secToHour, secToMinute, secToTwoDigitsHour, secToTwoDigitsMinute } from '../../../utils/date'
import Tips from '../common/Tips'

interface taskValue {
  task_id: number
  type: string
  origin_task_id: number | null
  time: number
  service_name: string | null
  icon: string | null
  icon_color: string | null
  task_name: string | null
  file_path: string | null
  included_url_parts: string | null
  exclusion_id: number | null
  timeline: {
    start_time: string
    end_time: string
  }[]
  label: {
    label_group_id: number
    label_group_name: string
    label_group_color: string
    label_id: number
    label_name: string
    label_color: string
    disabled: boolean
    status: string
  } | null
  is_loading?: boolean
}

interface LabelCommon {
  id: number
  name: string
  code: string | null
  color: string
}

interface Props {
  data: taskValue
  openCallback: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  delCallback: (labelGroupId: number, labelId: number) => void
  addCallback: (labelGroup: LabelCommon, label: LabelCommon, option?: string) => void
  delConfirmCallback: (labelGroupId: number, labelId: number) => void
  changeNameCallback: (taskId: number, name: string) => void
  delTaskCallback: (taskId: number, labelId: number, type: string) => void
  cutTaskCallback: (taskId: number) => void
  exclusionAutoLabelingTaskCallback: (taskId: number) => void
  originTitle: string | null
  clickCheckboxCallback: (taskId: number) => void
  isChecked: boolean
  isLocked: boolean
  isSideOpen: boolean
  date: string
  isLoading: boolean
  reservedLabelGroupId: number
  baseWorkStartSeconds: number
  baseWorkEndSeconds: number
  isDisplayBaseWorkTime: boolean
}

const LabelingModule: FC<Props> = (props) => {
  const {
    data,
    openCallback,
    delCallback,
    addCallback,
    delConfirmCallback,
    changeNameCallback,
    delTaskCallback,
    cutTaskCallback,
    exclusionAutoLabelingTaskCallback,
    originTitle,
    clickCheckboxCallback,
    isChecked,
    isLocked,
    isSideOpen,
    date,
    isLoading,
    reservedLabelGroupId,
    baseWorkStartSeconds,
    baseWorkEndSeconds,
    isDisplayBaseWorkTime,
  } = props

  const TIME_LIST = Array(25).fill(null).map((_v, i) => `${i + 5}:00`)

  const rulerBlock = () => (
    <div className="timeline_ruler_area">
      {
        TIME_LIST.map((v) => (
          <div key={`ruler_${v}`} />
        ))
      }
      <div />
    </div>
  )

  const inputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState<string>("")
  const [isEditName, setIsEditName] = useState<boolean>(false)
  const [isClickable, setIsClickable] = useState<boolean>(true)

  const [isOpenDetail, setIsOpenDetail] = useState<boolean>(false)

  const reservedLabelParams = useMemo(() => {
    if (data.label?.label_group_id === undefined) return {}
    if (data.label.label_group_id <= reservedLabelGroupId) {
      if (data.label.label_group_id === 0) {
        return {
          class: "not_belong",
          icon: "question",
        }
      }
      if (data.label.label_group_id === 1) {
        if (data.label.status === "auto") {
          return {
            class: "break",
            icon: "robot",
            description: "設定により休憩時間があらかじめ登録されました",
          }
        }
        return {
          class: "break",
          icon: "star",
        }
      }
      if (data.label.label_group_id === 2) {
        return {
          class: "not_working",
          icon: "exclamation-circle",
          description: "業務外に設定された作業データは仕分けデータ計算に使用されなくなります",
        }
      }
    }
    return {}
  }, [data])

  const bgClass = useMemo(() => {
    if (data.label && data.label.label_group_id === 1) {
      return "break"
    }
    if (data.label && data.label.label_group_id === 2) {
      return "not_working"
    }
    if (data.label && !(data.label.status === "suggestion" || data.label.status === "repeat_suggestion")) {
      return "labeled"
    }
    if (!data.label && data.exclusion_id) {
      return "exclusion"
    }
    return ""
  }, [data])

  const openName = () => {
    setIsEditName(true)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 100)
  }

  const closeName = () => {
    setIsEditName(false)
    setName(data.task_name || "")
  }

  const editName = () => {
    setIsEditName(false)
    changeNameCallback(data.task_id, name)
  }

  const deleteTask = () => {
    setIsClickable(false)
    delTaskCallback(data.task_id, data.label?.label_id || 0, "delete")
  }

  const undoTask = () => {
    setIsClickable(false)
    delTaskCallback(data.task_id, data.label?.label_id || 0, "reverse")
  }

  const buildTime = (dateTime: string, beforeDateTime: string) => {
    const tmpSec = dateToTodayElapsedSeconds(dateTime, beforeDateTime)
    return `${secToTwoDigitsHour(tmpSec)}時${secToTwoDigitsMinute(tmpSec)}分`
  }

  const buildTimelineClass = (param: {startSec: number, endSec: number}) => {
    if (
      isDisplayBaseWorkTime
      && (baseWorkStartSeconds && baseWorkEndSeconds)
      && (((baseWorkStartSeconds > param.startSec) && (baseWorkStartSeconds > param.endSec))
        || ((baseWorkEndSeconds < param.startSec) && (baseWorkEndSeconds < param.endSec))
      )
    ) {
      return "outside"
    }
    if (data.label && !(data.label.status === "suggestion" || data.label.status === "repeat_suggestion")) {
      return "labeled"
    }
    return ""
  }

  useEffect(() => {
    setName(data.task_name || "")
  }, [data])

  useEffect(() => {
    if (data.task_name) return
    setIsEditName(data.type !== "ActiveTask")
  }, [])

  return (
    <>
      <div
        className={
          `timeline_row ${bgClass} ${isOpenDetail ? "new" : ""}`
        }
        id={`task_id_${data.task_id}`}
        onMouseLeave={() => setIsOpenDetail(false)}
      >
        {rulerBlock()}
        {
          data.timeline.map((v) => ({
            ...v, start_sec: dateToTodayElapsedSeconds(v.start_time, date), end_sec: dateToTodayElapsedSeconds(v.end_time, date),
          })).map((value) => (
            <div
              key={`timeline_${value.start_time}`}
              className={
                `timeline_bar${data.type !== "ActiveTask" ? " inactive" : ""}
                ${buildTimelineClass({ startSec: value.start_sec, endSec: value.end_sec })}`
              }
              style={{
                left: `${((((value.start_sec - 18000) / 86400) * (5 / 6)) + (1 / 6)) * 100}%`,
                width:
                `${(((value.end_sec - value.start_sec) / 86400) * (5 / 6)) * 100}%`,
              }}
            >
              <Tips
                description={`${buildTime(value.start_time, date)} ~ ${buildTime(value.end_time, date)}`}
                className=""
                icon={false}
              >
                <></>
              </Tips>
            </div>
          ))
        }
        <div className="timeline_detail">
          <div className="name_area">
            {
              isEditName && !isLocked ? (
                <div className="input_area">
                  <input
                    ref={inputRef}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="作業データ名はなんですか？"
                    className={`${isSideOpen || "side_open"}`}
                  />
                  <button
                    type="button"
                    onClick={editName}
                    disabled={!name}
                  >
                    <FontAwesomeIcon icon={['fas', 'check']} />
                    &nbsp;
                    完了
                  </button>
                  <button
                    type="button"
                    onClick={closeName}
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                    &nbsp;
                    キャンセル
                  </button>
                </div>
              ) : (
                <div
                  className={`display_area ${isSideOpen || "side_open"}`}
                  title={`${data.service_name || ""} ${data.task_name || ""}`}
                >
                  <div
                    className="icon_area"
                    style={{
                      color: data.icon_color || (data.type !== "ActiveTask" && data.task_name ? "#008800" : "#FF0000"),
                    }}
                  >
                    {
                      data.icon ? (
                        <Tips
                          description={`${data.service_name || ""}`}
                          className="normal"
                          icon={false}
                        >
                          <FontAwesomeIcon
                            icon={[data.icon.split(" ")[0] as IconPrefix, data.icon.split(" ")[1] as IconName]}
                            fixedWidth
                          />
                        </Tips>
                      ) : (
                        <>
                          {
                            data.type === "OnlineMeetingTask" ? (
                              <FontAwesomeIcon
                                icon={["fas", "video"]}
                                fixedWidth
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={data.task_name ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                fixedWidth
                              />
                            )
                          }
                        </>
                      )
                    }
                  </div>
                  <div
                    className="text_area"
                    onClick={(data.type !== "ActiveTask" || data.origin_task_id) ? openName : undefined}
                    role="none"
                  >
                    {
                      data.origin_task_id ? (
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                            marginLeft: "4px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                        >
                          <span
                            style={{
                              backgroundColor: "#666666",
                              color: "#FFFFFF",
                              borderRadius: "4px",
                              padding: "0px 4px",
                              fontSize: "10px",
                              marginRight: "4px",
                            }}
                          >
                            元作業データ
                          </span>
                          {originTitle}
                        </div>
                      ) : (
                        <></>
                      )
                    }
                    {
                      data.service_name && data.origin_task_id ? (
                        <>
                          <FontAwesomeIcon
                            icon={['fas', 'reply']}
                            rotation={180}
                            style={{ fontSize: "12px", color: "#333333", margin: "0px 4px 0 8px" }}
                          />
                        </>
                      ) : (
                        <>
                        </>
                      )
                    }
                    {data.task_name || (data.type !== "ActiveTask" ? "設定されていません" : `${data.service_name} - タイトルなし`)}
                    {
                      (data.type !== "ActiveTask" || data.origin_task_id) && !isLocked && (
                        <>
                          &nbsp;
                          <FontAwesomeIcon icon={['fas', 'edit']} />
                        </>
                      )
                    }
                    {
                      data.included_url_parts && (
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                          title={`自動仕分け条件: ${data.included_url_parts}に該当`}
                        >
                          {`自動仕分け条件: ${data.included_url_parts}に該当`}
                        </div>
                      )
                    }
                    {
                      data.file_path && (
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                          title={`ファイルパス: ${data.file_path}`}
                        >
                          {`(${data.file_path})`}
                        </div>
                      )
                    }
                  </div>
                </div>
              )
            }
          </div>
          {
            isLoading ? (
              <>
                <FontAwesomeIcon icon={['fas', 'spinner']} spin />
              </>
            ) : (
              <>
                {
                  (!data.label) && !isLocked && (
                    <button
                      className="add_label"
                      type="button"
                      onClick={(e) => openCallback(e)}
                    >
                      <FontAwesomeIcon icon={['fas', 'tag']} rotation={90} />
                      業務仕分けする
                    </button>
                  )
                }
                {
                  // eslint-disable-next-line max-len
                  data.label && !isLocked && !data.label.disabled && (data.label.status === "suggestion" || data.label.status === "repeat_suggestion") && (
                    <div className="label_wrapper pending">
                      <div className="label">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              color: "#FF0000",
                              fontWeight: "bold",
                            }}
                          >
                            もしかして:
                          </span>
                          <span className="dummy_label">
                            {`${data.label.label_group_name}:${data.label.label_name || ""}`}
                          </span>
                          ですか？
                        </div>
                        <div
                          className="done"
                          onClick={() => addCallback(
                            {
                              id: data.label?.label_group_id || 0,
                              name: data.label?.label_group_name || "",
                              code: null,
                              color: data.label?.label_group_color || "#666666",
                            },
                            {
                              id: data.label?.label_id || 0,
                              name: data.label?.label_name || "",
                              code: null,
                              color: data.label?.label_color || "#666666",
                            },
                            data.label?.status === "repeat_suggestion" ? "repeat_request" : "suggestion_accept",
                          )}
                          role="none"
                        >
                          <FontAwesomeIcon icon={['fas', 'check']} />
                          そうです
                        </div>
                        <div
                          className="delete"
                          onClick={() => delCallback(data.label?.label_group_id || 0, data.label?.label_id || 0)}
                          role="none"
                        >
                          <FontAwesomeIcon icon={['fas', 'times']} />
                          違います
                        </div>
                      </div>
                    </div>
                  )
                }
                {
                  isLocked && (
                    !data.label
                    || (data.label && !data.label.disabled && (data.label.status === "suggestion" || data.label.status === "repeat_suggestion"))
                  ) && (
                    <>
                      <div className="label_wrapper">
                        <div className="label">
                          未仕分け
                        </div>
                      </div>
                    </>
                  )
                }
                {
                  data.label && data.label.status === "auto" && data.label.label_group_id > reservedLabelGroupId && (
                    <Tips
                      description="自動仕分けルールにより自動仕分けされました"
                      className=""
                      icon={false}
                    >
                      <div className="label_wrapper auto">
                        <FontAwesomeIcon icon={['fas', 'robot']} className="fa-fw c_icon" />
                        <div className="label">
                          {`${data.label.label_group_name}:${data.label.label_name || ""}`}
                          {
                            !isLocked && (
                              <span
                                className="delete"
                                onClick={() => delCallback(data.label?.label_group_id || 0, data.label?.label_id || 0)}
                                role="none"
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </span>
                            )
                          }
                        </div>
                      </div>
                    </Tips>
                  )
                }
                {
                  data.label && data.label.status === "repeat" && data.label.label_group_id > reservedLabelGroupId && (
                    <Tips
                      description="過去の仕分け履歴から自動仕分けされました"
                      className=""
                      icon={false}
                    >
                      <div className="label_wrapper persistence">
                        <FontAwesomeIcon icon={['fas', 'history']} className="fa-fw c_icon" />
                        <div className="label">
                          {`${data.label.label_group_name}:${data.label.label_name || ""}`}
                          {
                            !isLocked && (
                              <span
                                className="delete"
                                onClick={() => delConfirmCallback(data.label?.label_group_id || 0, data.label?.label_id || 0)}
                                role="none"
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </span>
                            )
                          }
                        </div>
                      </div>
                    </Tips>
                  )
                }
                {
                  data.label
                  && data.label.label_group_id > reservedLabelGroupId
                  && (data.label.status === "manual" || data.label.status === "suggestion_accepted")
                  && (
                    <div className="label_wrapper">
                      <FontAwesomeIcon icon={['fas', 'hand-point-right']} className="fa-fw c_icon" />
                      <div className="label">
                        {`${data.label.label_group_name}:${data.label.label_name || ""}`}
                        {
                          !isLocked && (
                            <span
                              className="delete"
                              onClick={() => delCallback(data.label?.label_group_id || 0, data.label?.label_id || 0)}
                              role="none"
                            >
                              <FontAwesomeIcon icon={['fas', 'times']} />
                            </span>
                          )
                        }
                      </div>
                    </div>
                  )
                }
                {
                  data.label && data.label.label_group_id <= reservedLabelGroupId && (
                    <Tips
                      description={reservedLabelParams.description || ""}
                      className=""
                      icon={false}
                    >
                      <div className={`label_wrapper ${reservedLabelParams.class}`}>
                        <FontAwesomeIcon icon={['fas', reservedLabelParams.icon as IconName]} className="fa-fw c_icon" />
                        <div className="label">
                          {`${data.label.label_group_name}`}
                          {
                            !isLocked && (
                              <span
                                className="delete"
                                onClick={() => delCallback(data.label?.label_group_id || 0, data.label?.label_id || 0)}
                                role="none"
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </span>
                            )
                          }
                        </div>
                      </div>
                    </Tips>
                  )
                }
              </>
            )
          }
        </div>
        <div className="time_area">
          <div>
            <FontAwesomeIcon icon={['far', 'clock']} />
          </div>
          <div>
            {
              data.time >= 60 ? (
                <>
                  {
                    secToHour(data.time) ? (
                      <>
                        {`${secToHour(data.time)}時間`}
                      </>
                    ) : (
                      <></>
                    )
                  }
                  {
                    secToMinute(data.time) ? (
                      <>
                        {`${secToMinute(data.time)}分`}
                      </>
                    ) : (
                      <></>
                    )
                  }
                </>
              ) : (
                <>
                  1分未満
                </>
              )
            }
          </div>
          {
            !isLocked && (
              <>
                {
                  isClickable ? (
                    <>
                      {
                        data.type === "ManualTask" ? (
                          <Tips
                            description="作業データを削除する"
                            className=""
                            icon={false}
                          >
                            <button
                              type="button"
                              className="help del"
                              onClick={deleteTask}
                              style={{ padding: "0 8px" }}
                              disabled={!isClickable}
                            >
                              <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                            </button>
                          </Tips>
                        ) : (
                          <>
                            {
                              data.origin_task_id ? (
                                <Tips
                                  description="元の作業データに戻す"
                                  className=""
                                  icon={false}
                                >
                                  <button
                                    type="button"
                                    className="help undo"
                                    onClick={undoTask}
                                    style={{ padding: "0 8px" }}
                                    disabled={!isClickable}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'undo-alt']} />
                                  </button>
                                </Tips>
                              ) : (
                                <>
                                  <Tips
                                    description="その他の操作"
                                    className=""
                                    icon={false}
                                    hideTips={isOpenDetail}
                                  >
                                    <button
                                      type="button"
                                      className="help"
                                      onClick={() => setIsOpenDetail(!isOpenDetail)}
                                      style={{ padding: "0 8px", fontSize: "16px" }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'ellipsis-h']} />
                                    </button>
                                  </Tips>
                                  <div
                                    style={{
                                      position: "absolute",
                                      right: "-20px",
                                      top: "-50px",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      backgroundColor: "#FFFFFF",
                                      padding: "8px",
                                      display: isOpenDetail ? "flex" : "none",
                                      flexFlow: "column",
                                      gap: "8px",
                                      whiteSpace: "nowrap",
                                      fontSize: "12px",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="help"
                                      onClick={() => exclusionAutoLabelingTaskCallback(data.task_id)}
                                      style={{
                                        padding: "0",
                                        display: "flex",
                                        gap: "2px",
                                        color: data.exclusion_id ? "#0095b1" : "#FF0000",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      {
                                        data.exclusion_id ? (
                                          <>
                                            <FontAwesomeIcon icon={['fas', 'undo']} />
                                            この作業データを自動仕分け対象外設定を解除する
                                          </>
                                        ) : (
                                          <>
                                            <FontAwesomeIcon icon={['fas', 'ban']} />
                                            この作業データを自動仕分け対象外にする
                                          </>
                                        )
                                      }
                                    </button>
                                    <button
                                      type="button"
                                      className="help"
                                      onClick={() => cutTaskCallback(data.task_id)}
                                      style={{
                                        padding: "0",
                                        display: "flex",
                                        gap: "2px",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'cut']} />
                                      この作業データを切り出す
                                    </button>
                                  </div>
                                </>
                              )
                            }
                          </>
                        )
                      }
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="help"
                        style={{ padding: "0 8px", fontSize: "16px" }}
                        disabled
                      >
                        <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                      </button>
                    </>
                  )
                }
              </>
            )
          }
        </div>
        {
          !isLocked && (
            <>
              <div
                style={{
                  position: "absolute",
                  top: "12px",
                  left: "-30px",
                  fontSize: "18px",
                  color: "#666666",
                  cursor: "pointer",
                }}
                onClick={() => clickCheckboxCallback(data.task_id)}
                role="none"
              >
                {
                  isChecked ? (
                    <>
                      <FontAwesomeIcon icon={['far', 'check-square']} style={{ color: "#228b22" }} />
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={['far', 'square']} />
                    </>
                  )
                }
              </div>
            </>
          )
        }
      </div>
    </>
  )
}

export default LabelingModule
